<template>
    <div class="p-day-one__modal">
        <p v-if="noChartData">
            There are no matching claims to show a comparison.
        </p>
        <div v-else>
            <div class="flex-grid a-form-inputs">
                <div class="flex-grid__item">
                    <div class="p-day-one__chart-select-box-label">Compare Against:</div>
                    <div class="flex-grid__g-9">
                        <select v-model="chartType">
                            <option v-for="(text, type) in chartTypes" :value="type">
                                {{ text }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="flex-grid__item flex-grid--space-left">
                    <div class="p-day-one__chart-select-box-label u-r-margin-top-20">Filter:</div>
                        <div class="flex-grid__g-9">
                            <select v-model="filterAttr">
                                <option v-for="(text, type) in filterTypes" :value="type">
                                    {{ text }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            <bell-chart :chart-data="forChart" :user-percentile="userPercentile" :user-label="ordinalSuffixOf(userPercentile)"></bell-chart>

            <h2 class="u-margin-top-30">This Assessment</h2>
            <table class="a-table u-full-width">
                <tr>
                    <td>Value of your assessment ({{filterTypes[filterAttr]}})</td>
                    <td>{{currencyFormat(userValue, "£" , 0)}}</td>
                </tr>
                <tr>
                    <td>Percentile for this claim:</td>
                    <td>{{ordinalSuffixOf(userPercentile)}}</td>
                </tr>
            </table>
            <h2 class="u-margin-top-30">Values of Matched Claims</h2>
            <table class="a-table u-full-width">
                <tr>
                    <td>Full Range of Matched Claims</td>
                    <td>{{currencyFormat(scaleMin, "£" , 0)}} - {{currencyFormat(scaleMax, "£" , 0)}}</td>
                </tr>
                <tr>
                    <td>0-25th Percentile</td>
                    <td>{{minPercentileLabel}}</td>
                </tr>
                <tr>
                    <td>25th - 75th Percentile</td>
                    <td>{{midPercentileLabel}}</td>
                </tr>
                <tr>
                    <td>75th - 100th Percentile</td>
                    <td>{{maxPercentileLabel}}</td>
                </tr>
            </table>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import {mapGetters} from 'vuex';
    import BarChart from 'components/graphs/BarChart';
    import Control from 'components/form/Control';
    import CurrencyFormat from 'mixins/currencyFormat';
    import RoundValue from 'mixins/roundValue';
    import Modal from 'components/modal/Modal';
    import ModalInTheWild from 'components/modal/ModalInTheWild';
    import BellChart from "./BellChart";

    export default {
        name: 'reserve-performance-chart',
        components: {
            BellChart,
            BarChart,
            Control,
            Modal,
            ModalInTheWild
        },
        mixins: [
            CurrencyFormat,
            RoundValue
        ],
        data() {
            return {
                chartType: 'all_claims',
                filterAttr: 'combined'
            }
        },
        computed: {
            ...mapGetters('reserves/dayOneFatal', [
                'chartData',
                'totalReserve',
                'apportionedClaimantReserve',
                'apportionedDefenceReserve',
                'apportionedDamagesReserve',
                'noChartData'
            ]),
            forChart() {
                return _.values(this.chartData.groups[this.chartType].data[this.filterAttr]);
            },
            /**
             * Label for min percentile (0-25th)
             * @return {string}
             */
            minPercentileLabel() {
                return this.currencyFormat(this.scaleMin, "£" , 0) + " to " +
                    this.currencyFormat(this.scaleMin + ((this.scaleMax - this.scaleMin) * 0.25), "£" , 0);
            },
            /**
             * Label for mid range percentile (50-75th)
             * @return {string}
             */
            midPercentileLabel() {
                return this.currencyFormat(this.scaleMin + ((this.scaleMax - this.scaleMin) * 0.25), "£" , 0) + " to " +
                    this.currencyFormat(this.scaleMin + ((this.scaleMax - this.scaleMin) * 0.75), "£" , 0);
            },
            /**
             * Label for max percentile (>75th)
             * @return {string}
             */
            maxPercentileLabel() {
                return this.currencyFormat(this.scaleMin + ((this.scaleMax - this.scaleMin) * 0.75), "£" , 0) + " to " +
                    this.currencyFormat(this.scaleMax, "£" , 0);
            },

            /**
             * What value is the user at depending on the filter?
             **/
            userValue() {
                switch (this.filterAttr) {
                    case "combined":
                        return this.totalReserve;
                    case 'damages':
                        return this.apportionedDamagesReserve;
                    case "claimant-costs":
                        return this.apportionedClaimantReserve;
                    case "defence-costs":
                        return this.apportionedDefenceReserve;
                }
            },
            /**
             * What is the users percentile on the current range?
             *
             * @return {Number}
             */
            userPercentile() {
                let group = this.chartData.groups[this.chartType];
                if (!group) {
                    return null;
                }
                let min = this.scaleMin;
                let max = this.scaleMax;
                if (this.userValue > max) {
                    return 100;
                }
                if (this.userValue < min) {
                    return 0;
                }
                let range = max - min;
                let diff = this.userValue - min;
                return Math.round((diff / range) * 100);
            },
            /**
             * The minimum value for this chart and filter?
             * @return {Number}
             */
            scaleMin() {
                return this.chartData.groups[this.chartType].min[this.filterAttr];
            },
            /**
             * The max value for this chart and filter?
             * @return {Number}
             */
            scaleMax() {
                return this.chartData.groups[this.chartType].max[this.filterAttr];
            },
            /**
             * Chart types as a index list for the options.
             * @return {{}}
             */
            chartTypes() {
                return _.chain(this.chartData.groups)
                    .keys()
                    .keyBy(key => key)
                    .mapValues(_.startCase)
                    .value();
            },
            /**
             * Chart filters as a index list for the options.
             * @return {{}}
             */
            filterTypes() {
                return this.chartData.filters;
            },
        },
        methods: {
            /**
             * Stolen from here: https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
             * @param i
             * @return {string}
             */
            ordinalSuffixOf: function (i) {
                var j = i % 10,
                    k = i % 100;
                if (j == 1 && k != 11) {
                    return i + "st";
                }
                if (j == 2 && k != 12) {
                    return i + "nd";
                }
                if (j == 3 && k != 13) {
                    return i + "rd";
                }
                return i + "th";
            }
        }
    }
</script>



// WEBPACK FOOTER //
// ReservePerformanceChart.vue?77ce7d64